import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Contact, ScanSearch, ClipboardCheck, Ship, Download } from "lucide-react";

const TYPE_META = {
  classify: { icon: ScanSearch, cls: "text-blue-400 border-blue-500/40" },
  healthcheck: { icon: ClipboardCheck, cls: "text-amber-400 border-amber-500/40" },
  clearance: { icon: Ship, cls: "text-emerald-400 border-emerald-500/40" },
};

export default function Leads() {
  const { data = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data, refetchInterval: 15000 });

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "roket_leads.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3">
        <h1 className="font-heading text-lg font-semibold tracking-tight">Leads</h1>
        <span className="text-xs font-mono text-zinc-500">{data.length} captured from landing page</span>
        <button data-testid="export-leads-btn" onClick={exportCsv} disabled={data.length === 0}
          className="ml-auto flex items-center gap-2 text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-900 disabled:opacity-50 transition-colors duration-150">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </header>
      <div className="p-6">
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">Captured</th>
                <th className="text-left font-medium py-2 px-3">Type</th>
                <th className="text-left font-medium py-2 px-3">Email</th>
                <th className="text-left font-medium py-2 px-3">Detail</th>
                <th className="text-left font-medium py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((l) => {
                const m = TYPE_META[l.type] || { icon: Contact, cls: "text-zinc-400 border-zinc-700" };
                const detail = l.type === "clearance"
                  ? `${l.country} · ${l.cargo || ""}`
                  : l.type === "healthcheck"
                    ? `${l.payload?.lines ?? ""} lines`
                    : (l.payload?.description || l.payload?.hs_code || "");
                return (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-zinc-800/60 hover:bg-zinc-900 transition-colors duration-150">
                    <td className="py-2 px-3 font-mono text-zinc-500 text-xs">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm border ${m.cls}`}>
                        <m.icon className="h-3 w-3" /> {l.type}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-zinc-200">{l.email || "—"}</td>
                    <td className="py-2 px-3 text-zinc-400 text-xs max-w-[320px] truncate">{detail}</td>
                    <td className="py-2 px-3 font-mono text-xs text-zinc-500">{l.status}</td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr><td colSpan={5} className="py-16 text-center text-zinc-500">
                  <Contact className="h-8 w-8 mx-auto mb-3 text-zinc-700" />
                  <p className="text-sm">No leads yet. They'll appear as visitors use the landing-page tools.</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
